import { Textarea } from '@headlessui/react'
import clsx from 'clsx'
import { useState, useMemo, useRef } from 'react'
import Button from '../components/Button'
import { toastError } from '../components/Notification'
import SimpleLoader from '../components/SimpleLoader'
import ImageUploadZone from '../components/prompt/ImageUploadZone'
import ImageCardGrid from '../components/prompt/ImageCardGrid'
import { useImageStore } from '../hooks/useImageStore'
import {
    VISION_LLM_MODELS,
    DEFAULT_VISION_LLM_MODEL,
    getLlmModelLabel
} from '../models/LlmModel'
import { ImagePromptResult, blobToBase64, validateTotalPayloadSize } from '../util/imagePromptTesting'
import { evaluateMetrics } from '../util/promptTesting'
import { useTestLlmImageClassificationPromptsLazyQuery } from '../api/imageClassificationQuery'

const LS_IMAGE_PROMPT = 'LOCAL_STORAGE_IMAGE_PROMPT'
const LS_IMAGE_MODEL = 'LOCAL_STORAGE_IMAGE_MODEL'
const LS_IMAGE_BOUNDARY = 'LOCAL_STORAGE_IMAGE_MODERATION_BOUNDARY'
const DEFAULT_MODERATION_BOUNDARY = 0.75

export default function ImagePromptTestingPage() {
    const [prompt, setPrompt] = useState(localStorage.getItem(LS_IMAGE_PROMPT) || '')
    const [model, setModel] = useState(localStorage.getItem(LS_IMAGE_MODEL) || DEFAULT_VISION_LLM_MODEL)
    const [moderationBoundary] = useState(
        parseFloat(localStorage.getItem(LS_IMAGE_BOUNDARY) || String(DEFAULT_MODERATION_BOUNDARY))
    )
    const [promptResults, setPromptResults] = useState<ImagePromptResult[]>([])
    const [testingLoading, setTestingLoading] = useState(false)
    const [processedCount, setProcessedCount] = useState(0)
    const [rerunningFileName, setRerunningFileName] = useState<string | null>(null)

    // Track whether the prompt has changed since the last test run
    const lastTestedPromptRef = useRef<string | null>(null)
    const resultsStale = promptResults.length > 0 && lastTestedPromptRef.current !== prompt

    const imageStore = useImageStore()
    const [testLlmImageClassification] = useTestLlmImageClassificationPromptsLazyQuery()

    // Persist to localStorage on change
    localStorage.setItem(LS_IMAGE_PROMPT, prompt)
    localStorage.setItem(LS_IMAGE_MODEL, model)
    localStorage.setItem(LS_IMAGE_BOUNDARY, String(moderationBoundary))

    const imagesWithTargets = imageStore.images.filter((img) => img.target !== null)
    const canTest = prompt.trim() !== '' && imagesWithTargets.length > 0

    // Build a Map from fileName -> result for quick lookup by the card grid
    const resultsMap = useMemo(() => {
        const map = new Map<string, ImagePromptResult>()
        for (const r of promptResults) {
            map.set(r.fileName, r)
        }
        return map
    }, [promptResults])

    const handleFilesSelected = async (files: File[]) => {
        try {
            await imageStore.saveImages(files)
        } catch (error) {
            toastError(error instanceof Error ? error.message : 'Failed to save images')
        }
    }

    const handleOnTest = async () => {
        const blobs = imagesWithTargets.map((img) => img.blob)
        const validation = validateTotalPayloadSize(blobs)
        if (!validation.valid) {
            toastError(validation.message || 'Payload too large')
            return
        }

        setPromptResults([])
        setProcessedCount(0)
        setTestingLoading(true)

        try {
            const testCases = await Promise.all(
                imagesWithTargets.map(async (img) => ({
                    imageBase64: await blobToBase64(img.blob),
                    mimeType: img.mimeType,
                    fileName: img.name,
                    target: img.target as string,
                }))
            )

            const { data } = await testLlmImageClassification({
                variables: { prompt, testCases, model },
            })

            if (data) {
                const results: ImagePromptResult[] =
                    data.testLlmImageClassificationPrompts.map((res) => ({
                        fileName: res.fileName,
                        target: res.target as 'Yes' | 'No',
                        score: res.score ?? null,
                        justification: res.justification,
                    }))
                setPromptResults(results)
                setProcessedCount(results.length)
                lastTestedPromptRef.current = prompt
            }
        } catch (error) {
            console.error('Error testing image classification:', error)
            toastError('Failed to test image classification')
        } finally {
            setTestingLoading(false)
        }
    }

    const handleRerunSingle = async (fileName: string) => {
        const image = imageStore.images.find((img) => img.name === fileName)
        if (!image || !image.target) return

        setRerunningFileName(fileName)
        try {
            const testCases = [{
                imageBase64: await blobToBase64(image.blob),
                mimeType: image.mimeType,
                fileName: image.name,
                target: image.target,
            }]
            const { data } = await testLlmImageClassification({
                variables: { prompt, testCases, model },
            })
            if (data && data.testLlmImageClassificationPrompts.length > 0) {
                const res = data.testLlmImageClassificationPrompts[0]
                const newResult: ImagePromptResult = {
                    fileName: res.fileName,
                    target: res.target as 'Yes' | 'No',
                    score: res.score ?? null,
                    justification: res.justification,
                }
                setPromptResults((prev) =>
                    prev.map((r) => (r.fileName === fileName ? newResult : r))
                )
            }
        } catch (error) {
            console.error('Error re-running image classification:', error)
            toastError('Failed to re-run classification')
        } finally {
            setRerunningFileName(null)
        }
    }

    const hasResults = promptResults.length > 0
    const { accuracy, precision, recall, f1 } = evaluateMetrics(promptResults, moderationBoundary)

    return (
        <div className="not-prose">
            {/* Prompt textarea */}
            <div className="mt-6">
                <label className="block text-sm font-medium leading-6 text-gray-900">Prompt</label>
                <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className={clsx(
                        'mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                        'ring-1 ring-inset ring-gray-300 placeholder:text-gray-400',
                        'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                    )}
                    rows={4}
                />
            </div>

            {/* Model dropdown */}
            <div className="mt-4">
                <label className="block text-sm font-medium leading-6 text-gray-900">Model</label>
                <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className={clsx(
                        'mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                        'ring-1 ring-inset ring-gray-300',
                        'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                    )}
                >
                    {VISION_LLM_MODELS.map((m) => (
                        <option key={m.id} value={m.id}>
                            {getLlmModelLabel(m)}{m.isDefault ? ' (default)' : ''}
                        </option>
                    ))}
                </select>
            </div>

            {/* Stale results banner */}
            {resultsStale && (
                <div className="mt-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                    Prompt has changed since these results were generated. Click <strong>Test</strong> or <strong>Re-run</strong> to update.
                </div>
            )}

            {/* Metrics summary — shown when results exist */}
            {hasResults && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                    {[
                        { label: 'Accuracy', value: accuracy },
                        { label: 'Precision', value: precision },
                        { label: 'Recall', value: recall },
                        { label: 'F1 Score', value: f1 },
                    ].map((m) => (
                        <div key={m.label} className={clsx(
                            'bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 transition-opacity',
                            resultsStale && 'opacity-60'
                        )}>
                            <div className="text-xs text-gray-500 uppercase">{m.label}</div>
                            <div className="text-lg font-bold text-gray-900">{m.value}%</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Test Images */}
            <div className="mt-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 mb-1">Test Images</label>
                <ImageUploadZone onFilesSelected={handleFilesSelected} />
                <ImageCardGrid
                    images={imageStore.images}
                    onDelete={imageStore.deleteImage}
                    onTargetChange={imageStore.updateTarget}
                    results={resultsMap}
                    moderationBoundary={moderationBoundary}
                    onRerunSingle={handleRerunSingle}
                    rerunningFileName={rerunningFileName}
                    resultsStale={resultsStale}
                />
            </div>

            {/* Test button + progress */}
            <div className="mt-4 flex items-center gap-4">
                <Button primary={true} text="Test" onClick={handleOnTest} disabled={!canTest} />
                {testingLoading && (
                    <div className="flex items-center gap-2">
                        <SimpleLoader loading={true} />
                        <span className="text-sm text-gray-500">
                            {processedCount}/{imagesWithTargets.length} (
                            {imagesWithTargets.length > 0
                                ? Math.round((processedCount / imagesWithTargets.length) * 100)
                                : 0}%)
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}
